import { useIntl } from 'react-intl';
import { twMerge } from 'tailwind-merge';

import { Button } from '../../../../front/src/components/Button/Button';
import { RoundIconButton } from '../../../../front/src/components/Button/RoundIconButton';
import { ConfirmationPopover } from '../../../../front/src/components/Popover/ConfirmationPopover';
import { blockedMessages } from '../../../../front/src/i18n/blocked';
import { ReactComponent as IconBlockUser } from '../../../../front/src/icons/icon-block-user.svg';
import { useBlockedUsers } from '../../hooks/useBlockedUsers';

type BlockUserDefaultButtonProps = {
  userId: string;
  pseudo?: string;
  picture?: string;
  className?: string;
  onClickOverload?: (...args: any[]) => void;
};

type BlockUserTextButtonProps = BlockUserDefaultButtonProps & {
  type: 'text';
};

type BlockUserIconButtonProps = BlockUserDefaultButtonProps & {
  type: 'icon';
  iconClassName?: string;
  iconContainerClassName?: string;
};

type BlockUserButtonProps = BlockUserTextButtonProps | BlockUserIconButtonProps;

export const BlockUserButton = (props: BlockUserButtonProps) => {
  const { type, userId, pseudo, picture, className, onClickOverload } = props;

  const { formatMessage } = useIntl();
  const { isBlocked, block, unblock, isNative } = useBlockedUsers();

  if (!isNative || !userId) return null;

  const blocked = isBlocked(userId);

  const label = blocked ? blockedMessages.unblock : blockedMessages.block;

  const handleToggle = async () => {
    if (blocked) {
      await unblock(userId);
    } else {
      await block({ userId, pseudo, picture });
    }
    onClickOverload?.();
  };

  const button =
    type === 'text' ? (
      <Button
        variant="secondary"
        className={twMerge('py-1 px-3 text-sm', className)}
        onClick={handleToggle}
      >
        {formatMessage(label)}
      </Button>
    ) : (
      <RoundIconButton
        title={formatMessage(label)}
        tooltip={formatMessage(label)}
        icon={IconBlockUser}
        className={className}
        iconClassName={props.iconClassName}
        iconContainerClassName={props.iconContainerClassName}
      />
    );

  if (blocked) return button;

  const messageContent = pseudo
    ? formatMessage(blockedMessages.blockConfirmationPseudo, { pseudo })
    : formatMessage(blockedMessages.blockConfirmationDefault);

  return (
    <ConfirmationPopover
      messageContent={messageContent}
      contentClassName="semibold"
      onConfirm={handleToggle}
    >
      {button}
    </ConfirmationPopover>
  );
};
